import { useCallback, useState, type FC, type SVGProps } from "react"

import { XMarkIcon } from "@heroicons/react/16/solid"
import { createPortal } from "react-dom"
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"

import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalArbitrage from "~/api/hooks/useBatteryProposalArbitrage"
import useBatteryProposalPricing from "~/api/hooks/useBatteryProposalPricing"
import ContractSigning from "~/components/Icons/ContractSigning"
import Financing from "~/components/Icons/Financing"
import HomeAndUtilityInfo from "~/components/Icons/HomeAndUtilityInfo"
import Pricing from "~/components/Icons/Pricing"
import Share from "~/components/Icons/Share"
import EnvVars from "~/env-vars"

import { batteryArbitrageStatusView } from "./battery-arbitrage-status"
import { batterySetupEscapeCommand } from "./battery-setup-escape"
import {
  batterySetupSearchEntries,
  INCENTIVES_LOCKED_HINT,
  type BatterySetupSearchEntry,
} from "./battery-setup-search"
import { BatterySetupQuickSearch } from "./BatterySetupQuickSearch"
import {
  ExportBrochureAction,
  useBatteryBrochureExportAction,
} from "./ExportBrochureAction"
import {
  batteryContractPath,
  batteryProposalPath,
  batterySetupGroupPath,
  batterySetupStepPath,
  batterySharePath,
  groupFromPath,
  isBatteryContractPath,
  isBatterySetupMenuOpen,
  isBatterySharePath,
  SETUP_GROUPS,
  stepFillsViewport,
  stepFromPath,
  type BatterySetupGroupId,
  type BatterySetupRouteSuffix,
} from "./setup-steps"
import {
  GROUP_PARTS_LIST,
  PART_LINK_ACTIVE,
  PART_LINK_BASE,
  PART_LINK_INACTIVE,
  SETUP_MENU_WIDTH,
  STEP_LINK_ACTIVE,
  STEP_LINK_BASE,
  STEP_LINK_INACTIVE,
} from "../../ProposalSetup/proposal-setup-rail"
import type { ProposalSetupEscapeEvent } from "../../ProposalSetup/proposal-setup-shortcuts"
import { ProposalSetupAccountLinks } from "../../ProposalSetup/ProposalSetupAccountLinks"
import { useProposalSetupShortcuts } from "../../ProposalSetup/use-proposal-setup-shortcuts"
import type { BatteryDrawerLaunchState } from "../financing-status/battery-drawer-launch"
import { PINNED_BATTERY_PROPOSAL_THEME } from "../theme/battery-proposal-theme"
import { useDesktopViewport } from "../use-desktop-viewport"

const GROUP_ICONS: Record<BatterySetupGroupId, FC<SVGProps<SVGSVGElement>>> = {
  [`home-and-utility-info`]: HomeAndUtilityInfo,
  [`product-offering`]: Financing,
  [`pricing`]: Pricing,
  contract: ContractSigning,
  share: Share,
}

const ROUTE_PART_PATH: Record<
  BatterySetupRouteSuffix,
  (proposalId: string) => string
> = {
  contract: batteryContractPath,
  share: batterySharePath,
}

const IS_ROUTE_PART_ACTIVE: Record<
  BatterySetupRouteSuffix,
  (pathname: string) => boolean
> = {
  contract: isBatteryContractPath,
  share: isBatterySharePath,
}

const PART_DISABLED = `flex flex-col gap-0.5 text-[0.875rem] leading-[21px] font-display p-[7px] mx-[5px] text-bp-text-dim cursor-not-allowed`

export { INCENTIVES_LOCKED_HINT }
export const PRICING_OUT_OF_DATE_HINT = `Pricing out of date`

/**
 * The one-line note under a rail entry, when that entry has something to say.
 * Kept beside the rail so a new indicator is a case here rather than another
 * conditional threaded through the list. Each case keeps its own test id.
 */
const partHint = (
  partId: string,
  {
    arbitrageHint,
    pricingOutOfDate,
  }: { arbitrageHint: string | null; pricingOutOfDate: boolean },
): { testId: string; text: string } | null => {
  if (partId === `system-pricing` && pricingOutOfDate) {
    return {
      testId: `battery-setup-pricing-out-of-date`,
      text: PRICING_OUT_OF_DATE_HINT,
    }
  }
  if (partId === `arbitrage` && arbitrageHint != null) {
    return { testId: `battery-setup-arbitrage-hint`, text: arbitrageHint }
  }
  return null
}

export const BatterySetupMenu = (): JSX.Element | null => {
  const savingsEnabled = EnvVars.BATTERY_SAVINGS_ENABLED
  const { proposalId } = useParams<{ proposalId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isDesktop = useDesktopViewport()
  const isOpen = isBatterySetupMenuOpen(pathname)
  const step = stepFromPath(pathname)
  const activeGroupId = groupFromPath(pathname)?.id
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(``)
  const exportAction = useBatteryBrochureExportAction(proposalId ?? ``)
  // Same row the /pricing endpoint reads; reusing the cached proposal avoids a
  // second request just to gate the menu. Search needs it too, so Incentives
  // stays locked before the rail is open.
  const proposalQuery = useBatteryProposal(
    isOpen || searchOpen ? proposalId : undefined,
  )
  const hasPricing = proposalQuery.data?.proposalCost != null
  // The freshness indicator sits beside System Pricing, which only renders
  // while the Pricing group is expanded — fetch the status just for that case.
  const pricingQuery = useBatteryProposalPricing(
    isOpen && activeGroupId === `pricing` ? proposalId : undefined,
  )
  const pricingOutOfDate = pricingQuery.data?.pricingStatus === `out_of_date`
  // Same reasoning as the pricing indicator: only fetch while the group that
  // shows Battery Savings is expanded. The read never starts a calculation, but
  // it does poll while one is running, so keep it scoped.
  const arbitrageQuery = useBatteryProposalArbitrage(
    savingsEnabled && isOpen && activeGroupId === `home-and-utility-info`
      ? proposalId
      : undefined,
  )
  const arbitrageHint = arbitrageQuery.data
    ? batteryArbitrageStatusView(arbitrageQuery.data).railHint
    : null

  const setSearch = useCallback((open: boolean): void => {
    if (open) {
      setSearchQuery(``)
    }
    setSearchOpen(open)
  }, [])
  const escapeCommand = useCallback(
    (event: ProposalSetupEscapeEvent) =>
      batterySetupEscapeCommand({
        ...event,
        pathname,
        proposalId: proposalId ?? ``,
      }),
    [pathname, proposalId],
  )
  useProposalSetupShortcuts({
    enabled: proposalId != null,
    escapeCommand,
    searchOpen,
    setSearchOpen: setSearch,
  })

  const onActivateSearch = (entry: BatterySetupSearchEntry): void => {
    switch (entry.command.kind) {
      case `navigate`:
        navigate(entry.command.to, { state: entry.command.state })
        setSearchOpen(false)
        return
      case `locked`:
        return
      case `export`:
        if (exportAction.start() === `started`) {
          setSearchOpen(false)
        }
        return
      default: {
        const _exhaustive: never = entry.command
        return _exhaustive
      }
    }
  }

  if (!proposalId) {
    return null
  }

  if (typeof document === `undefined`) {
    return null
  }

  const searchEntries = batterySetupSearchEntries({
    hasPricing,
    proposalId,
    savingsEnabled,
  })
  const search = searchOpen ? (
    <BatterySetupQuickSearch
      entries={searchEntries}
      exportPopupBlocked={exportAction.popupBlocked}
      onActivate={onActivateSearch}
      onClose={() => setSearchOpen(false)}
      query={searchQuery}
      setQuery={setSearchQuery}
    />
  ) : null

  // A step drawer that stretches to nearly the full viewport sits under the
  // fixed Setup rail (same z-layer, later in the DOM), so the rail yields until
  // the drawer closes. Below lg every step drawer is that wide; at any width
  // Greenbutton Data is. The narrow right-anchored drawers leave the rail
  // visible on desktop for direct step switching. Search stays available.
  if (stepFillsViewport(step)) {
    return search
  }
  if (
    !isDesktop &&
    (step != null ||
      isBatteryContractPath(pathname) ||
      isBatterySharePath(pathname))
  ) {
    return search
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-y-0 left-0 z-[2147483000] w-0 print:hidden"
      data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
    >
      {search}
      {/* Contract Status keeps the Setup rail open but hosts its own drawer at
          the same z-layer; a full-screen dismiss link would cover it. */}
      {isOpen &&
      step == null &&
      !isBatteryContractPath(pathname) &&
      !isBatterySharePath(pathname) ? (
        <Link
          to={batteryProposalPath(proposalId)}
          aria-label="Dismiss setup"
          className="pointer-events-auto fixed inset-0"
        />
      ) : null}
      {isOpen ? (
        <NavLink
          data-testid="battery-setup-menu-button"
          to={batteryProposalPath(proposalId)}
          aria-label="Close setup"
          className="pointer-events-auto absolute top-[30px] left-[10px] z-[1000] flex h-[35px] w-[35px] items-center justify-center rounded-[5px] bg-transparent p-[5px] text-bp-text no-underline"
        >
          <XMarkIcon className="h-10 w-10" />
        </NavLink>
      ) : null}
      {isOpen ? (
        <nav
          aria-label="Setup"
          className={`pointer-events-auto fixed top-0 left-0 flex h-full ${SETUP_MENU_WIDTH} flex-col overflow-y-auto border-r border-bp-border-strong bg-bp-elevated p-5 [&>*]:shrink-0`}
        >
          <h1 className="font-display pt-[18px] pb-6 text-center text-xl font-semibold text-bp-text capitalize">
            Setup
          </h1>
          <div className="mx-auto mb-5 h-px w-[95%] bg-bp-border-strong" />
          <ol className="flex w-full list-none flex-col">
            {SETUP_GROUPS.map((group) => {
              const Icon = GROUP_ICONS[group.id]
              const isActive = group.id === activeGroupId
              return (
                <li key={group.id} className="flex flex-col">
                  <Link
                    to={batterySetupGroupPath(proposalId, group.id)}
                    className={`${STEP_LINK_BASE} ${isActive ? STEP_LINK_ACTIVE : STEP_LINK_INACTIVE}`}
                  >
                    <Icon />
                    {group.label}
                  </Link>
                  {isActive ? (
                    <ol className={GROUP_PARTS_LIST}>
                      {group.parts.map((part) => {
                        if (part.id === `arbitrage` && !savingsEnabled) {
                          return null
                        }
                        const hint = partHint(part.id, {
                          arbitrageHint,
                          pricingOutOfDate,
                        })
                        return (
                          <li key={part.id} className="flex flex-col">
                            {part.kind === `step` &&
                            part.requiresPricing &&
                            !hasPricing ? (
                              <span
                                aria-disabled="true"
                                data-testid={`battery-setup-part-${part.id}-disabled`}
                                className={PART_DISABLED}
                              >
                                {part.label}
                                <span className="text-[0.75rem] leading-tight text-bp-text-muted">
                                  {INCENTIVES_LOCKED_HINT}
                                </span>
                              </span>
                            ) : part.kind === `action` ? (
                              <ExportBrochureAction
                                exportAction={exportAction}
                                proposalId={proposalId}
                              />
                            ) : part.kind === `route` ? (
                              <NavLink
                                to={ROUTE_PART_PATH[part.pathSuffix](proposalId)}
                                state={
                                  {
                                    returnTo: batterySetupGroupPath(
                                      proposalId,
                                      group.id,
                                    ),
                                  } satisfies BatteryDrawerLaunchState
                                }
                                data-testid={`battery-setup-part-${part.id}`}
                                className={() =>
                                  `${PART_LINK_BASE} ${
                                    IS_ROUTE_PART_ACTIVE[part.pathSuffix](
                                      pathname,
                                    )
                                      ? PART_LINK_ACTIVE
                                      : PART_LINK_INACTIVE
                                  }`
                                }
                              >
                                {part.label}
                              </NavLink>
                            ) : (
                              <NavLink
                                to={batterySetupStepPath(proposalId, part.id)}
                                className={({ isActive: partActive }) =>
                                  `${PART_LINK_BASE} ${partActive ? PART_LINK_ACTIVE : PART_LINK_INACTIVE}`
                                }
                              >
                                {hint != null ? (
                                  <span className="flex flex-col gap-0.5">
                                    <span>{part.label}</span>
                                    <span
                                      data-testid={hint.testId}
                                      className="text-[0.75rem] leading-tight text-bp-gold"
                                    >
                                      {hint.text}
                                    </span>
                                  </span>
                                ) : (
                                  part.label
                                )}
                              </NavLink>
                            )}
                          </li>
                        )
                      })}
                    </ol>
                  ) : null}
                </li>
              )
            })}
          </ol>
          <ProposalSetupAccountLinks divider />
        </nav>
      ) : null}
    </div>,
    document.body,
  )
}
